/**
 * Divisa de ESCAPARATE: qué precio se ANUNCIA, y cuándo esa elección se guarda
 * (ADR-021, fase 3; ADR-030, enmienda).
 *
 * Política editorial para landings y fallback Dodo. Stripe usa un precio base
 * EUR en el paywall y deja la conversión local a Adaptive Pricing (ADR-042).
 *
 * Por qué el país y no `navigator.language`: el idioma del móvil no dice dónde
 * compras. Un padre en México con el teléfono en `es-ES` veía euros (el caso
 * exacto que ADR-020 ya descartó para el gate del trial). El país llega del
 * GeoIP del edge, que sí describe la petición.
 *
 * Dominio puro a propósito: la política es la MISMA en servidor y cliente, y
 * aquí se puede probar sin React ni jsdom (y entra en el scope de Stryker).
 */

import { parseCountryCodes, type CountryCode } from '@/server/domain/country'
import { parsePriceCurrency, type PriceCurrency } from '@/server/domain/pricing'

/**
 * Países cuyo escaparate va en dólares: América, salvo los territorios que ya
 * usan euro. Es la lista que vivía duplicada en `use-currency-preference` y
 * dentro de `landing-pricing`, movida aquí sin cambiar a quién afecta — la fase
 * 3 cambia la SEÑAL (país del edge en vez de idioma del navegador), no el mapa.
 *
 * `ReadonlySet<CountryCode>` y no `string[]`: así `has()` solo acepta un
 * `CountryCode` —pasarle un idioma no compila— y la pertenencia es un hash en vez
 * de 29 comparaciones. Ojo: la lista se escribe como cadena y `parseCountryCodes`
 * la valida **en runtime**, descartando en silencio lo que no sea alpha-2. O sea
 * que un `'USA'` compila y simplemente desaparece del `Set`; de eso avisa el test
 * que fija el tamaño, no el compilador.
 *
 * Sigue siendo una aproximación editorial: BR, AR o CA no pagan en dólares, pero
 * el dólar es más reconocible ahí que el euro y hoy no tenemos escaparate en su
 * divisa. Con Dodo esta lista es solo el fallback de lo que se PINTA mientras el
 * proveedor decide lo que se COBRA (el arreglo de fondo fue la fase 5: pedir el
 * precio al proveedor).
 *
 */
export const USD_SHOWCASE_COUNTRIES: ReadonlySet<CountryCode> = new Set(
  parseCountryCodes(
    'US,MX,AR,BR,CO,CL,PE,VE,EC,BO,PY,UY,CR,PA,DO,GT,HN,NI,SV,CU,JM,HT,TT,BB,BS,BZ,GY,SR,CA'
  )
)

/**
 * País desconocido ⇒ EUR. A diferencia de los gates de coste (ADR-020 regla 4,
 * fail-closed), aquí no hay nada que proteger: no saber el país solo significa
 * anunciar la divisa del mercado principal.
 */
export function displayCurrencyForCountry(country: CountryCode | null): PriceCurrency {
  if (country === null) return 'EUR'

  return USD_SHOWCASE_COUNTRIES.has(country) ? 'USD' : 'EUR'
}

/** Las tres señales que pueden decidir la divisa anunciada, en crudo. */
export interface DisplayCurrencySignals {
  /** Lo que el usuario eligió y quedó guardado. */
  chosen: string | null
  /** `?currency=` de la URL: intención de una campaña. */
  fromUrl: string | null
  /** País del edge (ADR-020). */
  country: CountryCode | null
}

/**
 * Resuelve la divisa anunciada y **si esa resolución debe persistirse**.
 *
 * `persist` es un dato, no un efecto: antes la regla vivía en cuál de los dos
 * setters de React llamaba el hook (`setCurrency` guardaba, `setCurrencyState`
 * no), o sea que respetar el invariante del ADR dependía de acordarse de cuatro
 * letras al final de un identificador. Aquí es una tabla que un test lee.
 *
 * Solo se persiste la intención EXPLÍCITA (`?currency=`). Guardar la deducción
 * por país la volvería indistinguible de una elección: quien pasa una vez por
 * una VPN —o viaja— se quedaría con esa divisa congelada y el edge no podría
 * corregirla nunca más.
 */
export function resolveDisplayCurrency(
  signals: DisplayCurrencySignals
): { currency: PriceCurrency; persist: boolean } {
  const chosen = parsePriceCurrency(signals.chosen)
  if (chosen) return { currency: chosen, persist: false }

  const fromUrl = parsePriceCurrency(signals.fromUrl)
  if (fromUrl) return { currency: fromUrl, persist: true }

  return { currency: displayCurrencyForCountry(signals.country), persist: false }
}
